import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './Carousel.module.css';

export default function Carousel({ children, autoPlay = false, autoPlayInterval = 3000 }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);

  const totalItems = React.Children.count(children);
  const maxIndex = Math.max(0, totalItems - itemsPerView);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 480) {
        setItemsPerView(1);
        setIsMobile(true);
      } else if (window.innerWidth <= 768) {
        setItemsPerView(2);
        setIsMobile(true);
      } else {
        setItemsPerView(3);
        setIsMobile(false);
      }
    };

    handleResize(); 
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && carouselRef.current) {
      const container = carouselRef.current;

      const handleUserInteraction = () => {
        setIsUserInteracting(true);
        setTimeout(() => setIsUserInteracting(false), 15000);
      };

      container.addEventListener("touchstart", handleUserInteraction, { passive: true });

      return () => {
        container.removeEventListener("touchstart", handleUserInteraction);
      };
    }
  }, [isMobile]);

  useEffect(() => {
    if (autoPlay && totalItems > itemsPerView && !isUserInteracting) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
      }, autoPlayInterval);
    }
    return () => autoPlayRef.current && clearInterval(autoPlayRef.current);
  }, [autoPlay, autoPlayInterval, maxIndex, totalItems, itemsPerView, isUserInteracting]);

  const goToNext = () => {
    if (isTransitioning || currentIndex >= maxIndex) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const goToPrev = () => {
    if (isTransitioning || currentIndex <= 0) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const goToSlide = (index) => {
    if (isTransitioning || index === currentIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 500);
  };

  const itemWidth = 100 / itemsPerView;

  useEffect(() => {
    if (isMobile && carouselRef.current) {
      const container = carouselRef.current;
      const itemWidthPx = container.offsetWidth / itemsPerView;
      container.scrollTo({
        left: currentIndex * itemWidthPx,
        behavior: "smooth",
      });
    }
  }, [currentIndex, isMobile, itemsPerView]);

  return (
    <div className={styles.carousel}>
      <div className={`${styles.carouselWrapper} ${isMobile ? styles.mobileScroll : ''}`}>
        <div
          ref={carouselRef}
          className={`${styles.carouselContainer} ${isMobile ? styles.mobileScroll : ''}`}
          style={!isMobile ? { transform: `translateX(-${currentIndex * itemWidth}%)` } : {}}
        >
          {React.Children.map(children, (child, index) => (
            <div
              key={index}
              className={styles.carouselItem}
              style={{ minWidth: `${itemWidth}%` }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {!isMobile && totalItems > itemsPerView && (
        <>
          <button
            className={`${styles.carouselButton} ${styles.prevButton}`}
            onClick={goToPrev}
            disabled={currentIndex <= 0}
          >
            <ChevronLeft size={20} />
          </button>
          <button
            className={`${styles.carouselButton} ${styles.nextButton}`}
            onClick={goToNext}
            disabled={currentIndex >= maxIndex}
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {!isMobile && totalItems > itemsPerView && (
        <div className={styles.indicators}>
          {Array.from({ length: maxIndex + 1 }, (_, index) => (
            <button
              key={index}
              className={`${styles.indicator} ${
                index === currentIndex ? styles.indicatorActive : ''
              }`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
